import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useWorkspaceDetails } from '@/features/workspace/hooks/useWorkspaceDetails'
import { useSelectionStore } from '@/state/selection.store'
import { useUIStore } from '@/state/ui.store'
import { changeLicense } from '../api'
import { formatPlanDisplayName } from '@/lib/planDisplay'

/**
 * LicenseAssignment - Displays license assignment information and actions
 * Only shown in project context (not workspace level)
 * 
 * @param {boolean} isScrolled - Whether the page has been scrolled (for compact mode)
 */
export default function LicenseAssignment({ isScrolled = false }) {
    const queryClient = useQueryClient()
    const { workspace, refetch: refetchWorkspace } = useWorkspaceDetails()
    const currentProject = useSelectionStore(state => state.currentProject)
    const activeWorkspaceId = useSelectionStore(state => state.activeWorkspaceId)
    const setShowPricingModal = useUIStore(state => state.setShowPricingModal)

    const [loading, setLoading] = useState(false)

    const projectOrder = currentProject?.order || {}
    const projectUniqueUrl = currentProject?.uniqueUrl || currentProject?.unique_url || ''

    // Get total available licenses from workspace endpoint
    const totalAvailableLicenses = Number(workspace?.licenses?.totalAvailableLicenses || 0)

    // Get active/used licenses from workspace paidProjects
    const activeLicenses = Number(workspace?.paidProjects?.totalCount || 0)

    // Calculate remaining licenses
    const remainingLicenses = Math.max(totalAvailableLicenses - activeLicenses, 0)

    // Check for the presence of lifetime licenses from workspace
    const hasLifetimeLicense = totalAvailableLicenses > 0

    // Early return if not a lifetime license user
    if (!hasLifetimeLicense) {
        return null
    }

    // Get workspace active plans to display correct plan name
    const activePlans = workspace?.activePlans || (workspace?.plans || []).filter(p => p?.isActive)
    const activePlan = activePlans[0]
    // Fallback to project plan only if workspace plan isn't found (though for this component to show, workspace usually has a plan)
    const displayPlanName = formatPlanDisplayName(activePlan?.planName || activePlan?.name || projectOrder?.planName || projectOrder?.plan || 'Lifetime')
    const licenseType = 'lifetime'

    // Check if this project has a license assigned from workspace
    const projectHasLicense = projectOrder?.paid && projectOrder?.licenseSource === 'workspace'

    // Refetch project data reactively (no page reload needed)
    const refetchProjectData = async () => {
        // Invalidate & refetch the projects query so currentProject.order updates
        await queryClient.invalidateQueries({ queryKey: ['projects', activeWorkspaceId] })
        // Also refetch workspace details for license counts
        if (refetchWorkspace) await refetchWorkspace()
    }

    const handleAssignLicense = async () => {
        if (loading || !projectUniqueUrl) return
        setLoading(true)
        try {
            await changeLicense({ action: 'add', licenseType, project: projectUniqueUrl })
            await refetchProjectData()
            // Grab the stored callback before closing the modal (closing clears it)
            const onComplete = useUIStore.getState().pricingModalCallback
            // Close the pricing modal
            setShowPricingModal(false)
            // Invoke the callback if one was stored (e.g., proceed with domain connection)
            if (typeof onComplete === 'function') {
                onComplete()
            }
        } catch (err) {
            console.error('[LicenseAssignment] Failed to assign license:', err.message)
        } finally {
            setLoading(false)
        }
    }

    const handleUnassignLicense = async () => {
        if (loading || !projectUniqueUrl) return
        setLoading(true)
        try {
            await changeLicense({ action: 'remove', licenseType, project: projectUniqueUrl })
            await refetchProjectData()
        } catch (err) {
            console.error('[LicenseAssignment] Failed to unassign license:', err.message)
        } finally {
            setLoading(false)
        }
    }

    if (isScrolled) {
        return (
            <div style={styles.minimizedContainer}>
                <div style={styles.minimizedContent}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ ...styles.heading, color: '#FFFFFF' }}>{projectHasLicense ? 'License assigned ✓' : remainingLicenses > 0 ? `${remainingLicenses} ${remainingLicenses === 1 ? 'license' : 'licenses'} available` : 'All licenses in use'}</span>
                    </div>
                    
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                         {projectHasLicense ? (
                            <button
                                style={{ ...styles.button, backgroundColor: '#f3f4f6', color: '#1f2937', padding: '4px 12px', fontSize: '13px', opacity: loading ? 0.7 : 1 }}
                                onClick={handleUnassignLicense}
                                disabled={loading}
                            >
                                {loading ? 'Removing...' : 'Unassign'}
                            </button>
                        ) : remainingLicenses > 0 ? (
                            <button
                                style={{ ...styles.button, padding: '4px 12px', fontSize: '13px', opacity: loading ? 0.7 : 1 }}
                                onClick={handleAssignLicense}
                                disabled={loading}
                            >
                                {loading ? 'Assigning...' : 'Assign to Project'}
                            </button>
                        ) : (
                            <button style={{ ...styles.button, opacity: 0.5, cursor: 'not-allowed', padding: '4px 12px', fontSize: '13px' }} disabled>
                                None Available
                            </button>
                        )}
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div style={styles.container}>
            <div style={styles.card}>
                <div style={styles.innerContainer}>
                    {/* Left Side: Heading + Subtext */}
                    <div style={styles.textSection}>
                        <h3 style={styles.heading}>{projectHasLicense ? 'License assigned ✓' : remainingLicenses > 0 ? `${remainingLicenses} ${remainingLicenses === 1 ? 'license' : 'licenses'} available` : 'All licenses in use'}</h3>
                        <p style={styles.subtext}>
                            {projectHasLicense ? (
                                <>
                                    This project is already on your <span style={{ fontWeight: 600, color: '#09090B' }}>{displayPlanName}</span> — all premium features are unlocked.
                                    <br />
                                    Need this license elsewhere? Unassign it to free it up.
                                </>
                            ) : remainingLicenses > 0 ? (
                                <>
                                    You have <span style={{ fontWeight: 600, color: '#09090B' }}>{remainingLicenses} {remainingLicenses === 1 ? 'license' : 'licenses'}</span> from your <span style={{ fontWeight: 600, color: '#09090B' }}>{displayPlanName}</span> ready to use.
                                    <br />
                                    Assign one to this project to unlock all premium features.
                                </>
                            ) : (
                                <>
                                    All <span style={{ fontWeight: 600, color: '#09090B' }}>{totalAvailableLicenses}</span> of your <span style={{ fontWeight: 600, color: '#09090B' }}>{displayPlanName}</span> licenses are assigned to other projects.
                                    <br />
                                    Free one up by unassigning from another project, or upgrade your plan.
                                </>
                            )}
                        </p>
                    </div>

                    {/* Right Side: Counter + Button */}
                    <div style={styles.actionSection}>
                        <span style={styles.counter}>
                            {activeLicenses}/{totalAvailableLicenses}
                        </span>
                        
                        {projectHasLicense ? (
                            <button
                                style={{ ...styles.button, backgroundColor: '#f3f4f6', color: '#1f2937', opacity: loading ? 0.7 : 1 }}
                                onClick={handleUnassignLicense}
                                disabled={loading}
                            >
                                {loading ? 'Removing...' : 'Unassign License'}
                            </button>
                        ) : remainingLicenses > 0 ? (
                            <button
                                style={{ ...styles.button, opacity: loading ? 0.7 : 1 }}
                                onClick={handleAssignLicense}
                                disabled={loading}
                            >
                                {loading ? 'Assigning...' : 'Assign to Project'}
                            </button>
                        ) : (
                            <button style={{ ...styles.button, opacity: 0.5, cursor: 'not-allowed' }} disabled>
                                None Available
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <div style={styles.footerText}>
                or if you&apos;d like, you can purchase/upgrade below.
            </div>
        </div>
    )
}

const styles = {
    container: {
        display: 'flex',
        width: '100%',
        padding: '36px 0 24px 0',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '16px',
        backgroundColor: '#2A1980',
        fontFamily: "'Inter', sans-serif",
        transition: 'all 0.3s ease',
    },
    minimizedContainer: {
        display: 'flex',
        width: '100%',
        padding: '12px 24px',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#2A1980',
        fontFamily: "'Inter', sans-serif",
        position: 'sticky',
        top: 0,
        zIndex: 50,
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    },
    minimizedContent: {
        display: 'flex',
        width: '100%',
        maxWidth: '603px',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    card: {
        display: 'flex',
        width: '100%',
        maxWidth: '603px',
        padding: '16px',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'flex-start',
        borderRadius: '12px',
        backgroundColor: '#FFF',
        boxShadow: '0 10px 16px 0 rgba(28, 28, 35, 0.02), 0 6px 10px 0 rgba(28, 28, 35, 0.04), 0 0 3px 0 rgba(28, 28, 35, 0.09)',
        boxSizing: 'border-box',
    },
    innerContainer: {
        display: 'flex',
        justifyContent: 'space-between', // Changed to space-between for better separation
        alignItems: 'flex-end', // Align bottom as per spec
        gap: '32px',
        width: '100%',
    },
    textSection: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'flex-start',
        gap: '4px', // Reduced gap for tighter heading/subtext
        overflow: 'hidden',
        flex: 1,
    },
    heading: {
        margin: 0,
        color: '#09090B',
        fontFamily: 'Inter',
        fontSize: '16px',
        fontStyle: 'normal',
        fontWeight: 600,
        lineHeight: '24px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    subtext: {
        margin: 0,
        color: '#A1A1AA', // contentTertiary
        fontFamily: 'Inter',
        fontSize: '12px',
        fontStyle: 'normal',
        fontWeight: 500,
        lineHeight: '16px',
        // Note: Spec said nowrap, but for subtext with a break, likely meant to wrap or be multi-line. 
        // Keeping it normal wrapping for readability unless forced otherwise.
    },
    actionSection: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
        gap: '8px',
        flexShrink: 0,
    },
    counter: {
        color: '#09090B',
        textAlign: 'right',
        fontFamily: 'Inter',
        fontSize: '12px',
        fontStyle: 'normal',
        fontWeight: 600,
        lineHeight: '16px',
    },
    button: {
        display: 'flex',
        padding: '6px 10px',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '8px',
        borderRadius: '8px',
        background: '#5C33FF',
        border: 'none',
        color: '#FFF',
        fontFamily: 'Inter',
        fontSize: '14px',
        fontStyle: 'normal',
        fontWeight: 600,
        lineHeight: '20px',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
    },
    footerText: {
        color: '#FFF', // contentInversePrimary
        textAlign: 'center',
        fontFamily: 'Inter',
        fontSize: '12px',
        fontStyle: 'normal',
        fontWeight: 500,
        lineHeight: '16px',
    },
}
